import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Route, Routes, useLocation } from 'react-router';
import { useEffect } from 'react';
import { Header, Footer } from '@ui/siteStructure';
import { SidebarAds } from '@ui/ads/components/SidebarAds';
import { DeviceType } from '@repo/types';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CmsPage } from './pages/CmsPage';
import { ArticlePage } from './pages/ArticlePage';
import { FixturesPage } from './pages/FixturesPage';
import { ResultsPage } from './pages/ResultsPage';
import { TeamPage } from './pages/TeamPage';
import { MatchPage } from './pages/MatchPage/MatchPage';
import { NewsletterConfirm } from './pages/NewsletterConfirm';
import { useScrollToTop } from './utils/useScrollToTop';
import { ScrollToTop } from '@ui/common/components/ScrollToTop/ScrollToTop';
import { useDeviceType } from '@ui/utils/useDeviceType';
import { useThemeQuery } from '@data/settings/useThemeQuery';
import { useTheme } from './utils/useTheme';
import { ads } from './constants';
import './styles/theme.css';
import './styles/index.css';
import './styles/breakpoints.css';
import 'react-loading-skeleton/dist/skeleton.css';
import { ClubPage } from './pages/ClubPage';
import { MatchCentrePage } from './pages/MatchCentre/MatchCentre';
import { useGlobalScrollEvents } from './utils/useGlobalScrollEvents';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: QueryClient;
  }
}

function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { data: theme } = useThemeQuery();
  useTheme(theme);
  return <>{children}</>;
}

export function ClientApp() {
  const deviceType = useDeviceType();
  const location = useLocation();
  useScrollToTop();

  if (!import.meta.env.PROD && typeof window !== 'undefined') {
    window.__TANSTACK_QUERY_CLIENT__ = queryClient;
  }

  // HACK for removing the SM AI ads script from /live page and adding it back for rest of the pages
  useEffect(() => {
    // Check current route and manage script accordingly
    const manageScript = () => {
      const existingScript = document.getElementById('sm-ai-script');

      if (location.pathname === '/live') {
        // On /live page - remove the script
        if (existingScript) {
          existingScript.remove();
        }
      } else {
        // Not on /live page - add the script if it doesn't exist
        if (!existingScript) {
          // Use setTimeout to ensure script is added after navigation completes
          setTimeout(() => {
            const script = document.createElement('script');
            script.id = 'sm-ai-script';
            script.src = `https://cdn.browsiprod.com/bootstrap/bootstrap.js?t=${Date.now()}`;
            script.setAttribute('data-pubKey', 'SnackMedia');
            script.setAttribute('data-siteKey', 'd_mapping');

            // Force reload by removing async
            script.async = false;

            // Append to body instead of head for better execution
            document.body.appendChild(script);

            // Force a reflow to trigger script execution
            document.body.offsetHeight;
          }, 100);
        }
      }
    };

    // Run immediately to handle initial state
    manageScript();
  }, [location.pathname]);

  // Subscribe to url listeners for anchor hash changes that trigger smooth scrolling
  useGlobalScrollEvents();

  return (
    <HelmetProvider>
      <Helmet prioritizeSeoTags>
        <title>{import.meta.env.VITE_SITE_NAME}</title>
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:locale" content="en_GB" />
        <meta property="og:site_name" content={import.meta.env.VITE_SITE_NAME} />
        <meta name="twitter:site" content={import.meta.env.VITE_TWITTER_SITE} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <div className="page-wrapper">
            <Header />
            <div className="main-content-wrapper">
              <main>
                <Routes>
                  <Route path="/match/:id" element={<MatchPage />} />
                  <Route path="/clubs/:slug" element={<ClubPage />} />
                  <Route path="/teams/:slug" element={<TeamPage />} />
                  <Route path="/articles/:slug" element={<ArticlePage />} />
                  <Route path="/results" element={<ResultsPage />} />
                  <Route path="/fixtures" element={<FixturesPage />} />
                  <Route path="/live" element={<MatchCentrePage />} />
                  <Route path="/newsletter-confirm" element={<NewsletterConfirm />} />
                  <Route path="*" element={<CmsPage />} />
                </Routes>
                <ScrollToTop />
              </main>
              {deviceType === DeviceType.desktop ? (
                <SidebarAds ads={ads} />
              ) : (
                <div className="primis-video-ad" id="snack-vod" />
              )}
            </div>
            <Footer />
          </div>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}
